import React from 'react';
import { FilterOptions, TeacherOption, SquadOption, StudentOption } from '../../../shared/services/SessionsService';

interface FilterControlsProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  teachers: TeacherOption[];
  squads: SquadOption[];
  loading?: boolean;
  students?: StudentOption[];
}

const FilterControls: React.FC<FilterControlsProps> = ({
  filters,
  onFiltersChange,
  teachers,
  squads,
  loading = false,
  students = []
}) => {
  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const parseDateFromInput = (dateString: string) => {
    return dateString ? new Date(dateString + 'T00:00:00') : undefined;
  };

  const hasActiveFilters = Object.keys(filters).some(key => 
    filters[key as keyof FilterOptions] !== undefined && 
    filters[key as keyof FilterOptions] !== '' && 
    filters[key as keyof FilterOptions] !== null
  );

  return (
    <div className="filter-controls">
      <div className="filter-row">
        <div className="filter-main-controls">
          {/* Teacher Filter */}
          <div className="filter-inline">
            <label htmlFor="teacher-filter">Teacher</label>
            <select
              id="teacher-filter"
              value={filters.teacherId || ''}
              onChange={(e) => handleFilterChange('teacherId', e.target.value || undefined)}
              disabled={loading}
            >
              <option value="">All Teachers</option>
              {teachers.map(teacher => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </option>
              ))}
            </select>
          </div>

          {/* Squad Filter */}
          <div className="filter-inline">
            <label htmlFor="squad-filter">Squad</label>
            <select
              id="squad-filter"
              value={filters.squadId || ''}
              onChange={(e) => handleFilterChange('squadId', e.target.value || undefined)}
              disabled={loading}
            >
              <option value="">All Squads</option>
              {squads.map(squad => (
                <option key={squad.id} value={squad.id}>
                  {squad.title} ({squad.schoolName})
                </option>
              ))}
            </select>
          </div>

          {/* Date From Filter */}
          <div className="filter-inline">
            <label htmlFor="date-from-filter">From Date</label>
            <input
              type="date"
              id="date-from-filter"
              value={formatDateForInput(filters.dateFrom)}
              onChange={(e) => handleFilterChange('dateFrom', parseDateFromInput(e.target.value))}
              disabled={loading}
            />
          </div>

          {/* Date To Filter */}
          <div className="filter-inline">
            <label htmlFor="date-to-filter">To Date</label>
            <input
              type="date"
              id="date-to-filter"
              value={formatDateForInput(filters.dateTo)}
              onChange={(e) => handleFilterChange('dateTo', parseDateFromInput(e.target.value))}
              disabled={loading}
            />
          </div>

          {/* Location Status Filter */}
          <div className="filter-inline">
            <label htmlFor="location-filter">Location</label>
            <select
              id="location-filter"
              value={filters.locationStatus || ''}
              onChange={(e) => handleFilterChange('locationStatus', e.target.value || undefined)}
              disabled={loading}
            >
              <option value="">All</option>
              <option value="on-site">On-site ✅</option>
              <option value="near-site">Near-site ⚠️</option>
              <option value="off-site">Off-site ❌</option>
              <option value="unknown">Unknown ❓</option>
            </select>
          </div>

          {/* Minimum Attendance Filter */}
          <div className="filter-inline attendance-filter">
            <label htmlFor="attendance-filter">
              Min Attendance {filters.minAttendance ? `(${filters.minAttendance}%)` : ''}
            </label>
            <input
              type="range"
              id="attendance-filter"
              min="0"
              max="100"
              step="5"
              value={filters.minAttendance || 0}
              onChange={(e) => handleFilterChange('minAttendance', parseInt(e.target.value) || undefined)}
              disabled={loading}
              style={{ width: '80px' }}
            />
          </div>

          {/* Student Filter */}
          <div className="filter-inline">
            <label htmlFor="student-filter">Student</label>
            <select
              id="student-filter"
              value={filters.studentId || ''}
              onChange={(e) => handleFilterChange('studentId', e.target.value || undefined)}
              disabled={loading}
            >
              <option value="">All Students</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>{student.name}</option>
              ))}
            </select>
          </div>

          {/* Level Filter */}
          <div className="filter-inline">
            <label htmlFor="level-filter">Level</label>
            <input
              id="level-filter"
              type="number"
              min={1}
              step={1}
              placeholder="Any"
              value={filters.levelNumber ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                handleFilterChange('levelNumber', val === '' ? undefined : Math.max(1, parseInt(val, 10)));
              }}
              disabled={loading}
              style={{ width: '90px' }}
            />
          </div>

          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Quick Filter Buttons */}
      <div className="quick-filters">
        <h4>Quick Filters</h4>
        <div className="quick-filter-buttons">
          <button
            className={`quick-filter-btn ${!filters.dateFrom && !filters.dateTo ? 'active' : ''}`}
            onClick={() => onFiltersChange({ ...filters, dateFrom: undefined, dateTo: undefined })}
            disabled={loading}
          >
            All Time
          </button>
          <button
            className={`quick-filter-btn ${
              filters.dateFrom && 
              filters.dateFrom.getTime() === new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0)
              ? 'active' : ''
            }`}
            onClick={() => {
              const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
              weekAgo.setHours(0, 0, 0, 0);
              onFiltersChange({ 
                ...filters, 
                dateFrom: weekAgo,
                dateTo: new Date() 
              });
            }}
            disabled={loading}
          >
            Last 7 Days
          </button>
          <button
            className={`quick-filter-btn ${
              filters.dateFrom && 
              filters.dateFrom.getTime() === new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0)
              ? 'active' : ''
            }`}
            onClick={() => {
              const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
              monthAgo.setHours(0, 0, 0, 0);
              onFiltersChange({ 
                ...filters, 
                dateFrom: monthAgo,
                dateTo: new Date() 
              });
            }}
            disabled={loading}
          >
            Last 30 Days
          </button>
          <button
            className={`quick-filter-btn ${filters.minAttendance === 70 ? 'active' : ''}`}
            onClick={() => onFiltersChange({ 
              ...filters, 
              minAttendance: filters.minAttendance === 70 ? undefined : 70 
            })}
            disabled={loading}
          >
            Good Attendance (70%+)
          </button>
          <button
            className={`quick-filter-btn ${filters.locationStatus === 'off-site' ? 'active' : ''}`}
            onClick={() => onFiltersChange({ 
              ...filters, 
              locationStatus: filters.locationStatus === 'off-site' ? undefined : 'off-site'
            })}
            disabled={loading}
          >
            Off-site Sessions
          </button>
        </div>
      </div>

      {/* Filter Summary */}
      {hasActiveFilters && (
        <div className="filter-summary">
          <h4>Active Filters:</h4>
          <div className="active-filters">
            {filters.teacherId && (
              <span className="filter-tag">
                Teacher: {teachers.find(t => t.id === filters.teacherId)?.name}
                <button onClick={() => handleFilterChange('teacherId', undefined)}>×</button>
              </span>
            )}
            {filters.squadId && (
              <span className="filter-tag">
                Squad: {squads.find(s => s.id === filters.squadId)?.title}
                <button onClick={() => handleFilterChange('squadId', undefined)}>×</button>
              </span>
            )}
            {filters.dateFrom && (
              <span className="filter-tag">
                From: {filters.dateFrom.toLocaleDateString()}
                <button onClick={() => handleFilterChange('dateFrom', undefined)}>×</button>
              </span>
            )}
            {filters.dateTo && (
              <span className="filter-tag">
                To: {filters.dateTo.toLocaleDateString()}
                <button onClick={() => handleFilterChange('dateTo', undefined)}>×</button>
              </span>
            )}
            {filters.locationStatus && (
              <span className="filter-tag">
                Location: {filters.locationStatus}
                <button onClick={() => handleFilterChange('locationStatus', undefined)}>×</button>
              </span>
            )}
            {filters.minAttendance && (
              <span className="filter-tag">
                Min Attendance: {filters.minAttendance}%
                <button onClick={() => handleFilterChange('minAttendance', undefined)}>×</button>
              </span>
            )}
            {filters.levelNumber && (
              <span className="filter-tag">
                Level: {filters.levelNumber}
                <button onClick={() => handleFilterChange('levelNumber', undefined)}>×</button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;